import { useEffect, useRef } from "react";
import { logReferralClick } from "@/utils/referral.functions";

type Props = {
  /** Where on the page this widget is rendered. Used for referral-fee attribution. */
  source?: string;
  riskScore?: number | null;
  riskLevel?: "low" | "medium" | "high" | null;
  toolSlug?: string | null;
};

const SCRIPT_SRC = "https://www.avvo.com/assets/widgets-v2.js";

/**
 * Avvo "Find a Lawyer" widget (468x100).
 * Loads the official Avvo widgets-v2.js script once and lets it hydrate the
 * markup below. Click handler logs the click + appends `subid=<click_id>` to
 * the outbound URL so partner reports can be reconciled with our DB.
 */
export function AvvoWidget({
  source = "avvo-widget",
  riskScore = null,
  riskLevel = null,
  toolSlug = null,
}: Props = {}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)) return;
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const handleClick = async (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest<HTMLAnchorElement>("a[href]");
      if (!target || !root.contains(target)) return;
      // Only intercept Avvo URLs.
      let url: URL;
      try {
        url = new URL(target.href);
      } catch {
        return;
      }
      if (!/(^|\.)avvo\.com$/i.test(url.hostname)) return;

      e.preventDefault();
      let clickId: string | null = null;
      try {
        const res = await logReferralClick({
          data: {
            partner: "avvo",
            source,
            riskScore,
            riskLevel,
            toolSlug,
            referrerUrl: typeof window !== "undefined" ? window.location.href.slice(0, 2048) : null,
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 512) : null,
          },
        });
        clickId = res.click_id;
      } catch (err) {
        console.error("Failed to log referral click", err);
      }

      if (clickId) {
        // Avvo affiliate convention: pass our id as `subid` so we can reconcile.
        url.searchParams.set("subid", clickId);
        url.searchParams.set("utm_source", "legaldecoder");
        url.searchParams.set("utm_medium", "referral");
        url.searchParams.set("utm_campaign", source);
      }
      window.open(url.toString(), "_blank", "noopener,noreferrer");
    };

    root.addEventListener("click", handleClick);
    return () => root.removeEventListener("click", handleClick);
  }, [source, riskScore, riskLevel, toolSlug]);

  return (
    <div ref={containerRef} className="flex justify-center">
      <div className="avvo_widget size_468x100 avvo_wt_find_a_lawyer">
        <div className="avvo_header"></div>
        <div className="avvo_content">
          <a
            href="https://www.avvo.com/find-a-lawyer?utm_campaign=widget&utm_content=find-a-lawyer&utm_medium=syndication&utm_source=avvo"
            rel="noopener noreferrer"
          >
            Find a Lawyer
          </a>
          <a href="https://www.avvo.com" rel="noopener noreferrer">
            Avvo.com
          </a>
        </div>
      </div>
    </div>
  );
}
